import React, { useCallback, useMemo } from 'react'
import clsx from 'clsx'
import styled from 'styled-components'
import { rem } from 'polished'

import { CaptionMedium } from 'components/Luxkit/Typography/Caption'
import { formatOccupantsShort } from 'lib/offer/occupancyUtils'
import { sortBy } from 'lib/array/arrayUtils'
import BodyText from 'components/Luxkit/Typography/BodyText'
import LineEditAltIcon from 'components/Luxkit/Icons/line/LineEditAltIcon'
import { formatRoomType } from 'lib/tours/tourUtils'
import Heading from 'components/Luxkit/Typography/Heading'
import Pane from 'components/Common/Pane'
import SlideDown from 'components/Common/SlideDown'
import TextButton from 'components/Luxkit/Button/TextButton'

import RoomTypeOptionCard from './RoomOptionCard'
import FormatCurrency from 'components/Common/FormatCurrency'

const Root = styled(Pane)`
  border-radius: ${props => props.theme.borderRadius.M};
  transition: 0.2s ease padding, 0.2s ease border-color;

  & + &:not(:last-child):not(.has-option-selected):after,
  &:not(:last-child).is-active:after {
    content: "";
    display: block;
    border-bottom: 1px solid ${({ theme }) => theme.palette.neutral.default.six};
    margin-top: ${rem(16)};
  }

  &.is-active,
  &:not(.has-option-selected) {
    border-color: transparent;
    padding: 0;
  }

  &:not(.is-active).has-option-selected {
    padding: ${rem(20)} ${rem(24)};
  }
`

const Toolbar = styled.header`
  align-items: center;
  display: flex;
  justify-content: space-between;
  min-height: ${rem(36)}; /* to prevent layout shift when the edit button appears */
`

const ToolbarTitle = styled(Heading)`
  border-radius: ${props => props.theme.borderRadius.S};
  transition:
    0.2s ease background-color,
    0.2s ease font-size,
    0.2s ease line-height,
    0.2s ease padding;

  &.is-small-caption-tag { /* small Caption dark grey + Tag*/
    background-color: ${({ theme }) => theme.palette.neutral.default.six};
    font-family: ${props => props.theme.font.primary.family};
    font-weight: ${props => props.theme.font.primary.weight.regular};
    padding: ${rem(2)} ${rem(8)};
    ${CaptionMedium};
  }
`

const SelectedRoomTypeNameTitle = styled(Heading)`
  opacity: 0;
  transition: 0.5s ease opacity;

  &.is-expanded {
    opacity: 1;
  }
`

const OptionsList = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: ${rem(24)};
  transition: 0.3s ease margin-top;

  > * + * {
    margin-top: ${rem(12)};
    transition: 0.3s ease margin-top;
  }

  &.has-children-collapsed {
    margin-top: 0;

    > * + * {
      margin-top: 0;
    }
  }
`

const RoomDetailContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

interface Props {
  isCheckout?: boolean
  isActive?: boolean
  isEditable?: boolean
  occupants: App.Occupants
  options: Map<Tours.V2OfferPurchasableOption, Tours.V2OfferDeparturePriceBreakdown>
  occupantsIndex: number
  selectedPurchasableOption: Tours.V2OfferPurchasableOption | undefined
  onOccupantsOptionsEdit: (activeOccupantsIndex: number | 'next') => void
  onOccupantsPurchasableOptionSelect: (
    occupants: App.Occupants,
    purchasableOption: Tours.V2OfferPurchasableOption,
  ) => void
  onOccupantsPurchasableOptionUnselect: (occupants: App.Occupants) => void
}

function RoomOptionsList(props: Props) {
  const {
    isCheckout = false,
    isActive,
    isEditable,
    occupants,
    options,
    occupantsIndex,
    selectedPurchasableOption,
    onOccupantsOptionsEdit,
    onOccupantsPurchasableOptionSelect,
    onOccupantsPurchasableOptionUnselect,
  } = props

  const formattedOccupants: string = useMemo(
    () => formatOccupantsShort(occupants),
    [occupants],
  )

  const roomOptions = useMemo(
    () => sortBy(
      Array.from(options),
      ([, priceBreakdown]) => priceBreakdown.totalPrice,
      'asc',
    ),
    [options],
  )

  const currentSelectedPrice: number = useMemo(
    () => {
      if (selectedPurchasableOption) {
        return options.get(selectedPurchasableOption).totalPrice
      } else {
        const [, priceBreakdown] = roomOptions[0]

        return priceBreakdown.totalPrice
      }
    },
    [options, roomOptions, selectedPurchasableOption],
  )

  const selectedRoomTypeName: string | null = useMemo(
    () => selectedPurchasableOption ? formatRoomType(selectedPurchasableOption.roomType) : null,
    [selectedPurchasableOption],
  )

  const handlePurchasableOptionSelect = useCallback(
    (purchasableOption: Tours.V2OfferPurchasableOption) => {
      onOccupantsPurchasableOptionSelect(occupants, purchasableOption)
    },
    [onOccupantsPurchasableOptionSelect, occupants],
  )

  const handlePurchasableOptionUnselect = useCallback(
    () => {
      onOccupantsPurchasableOptionUnselect(occupants)
    },
    [onOccupantsPurchasableOptionUnselect, occupants],
  )

  const handleEdit = useCallback(() => onOccupantsOptionsEdit(occupantsIndex), [onOccupantsOptionsEdit, occupantsIndex])

  return <Root
    type="clean"
    axis="vertical"
    className={clsx({ 'is-active': isActive, 'has-option-selected': !!selectedPurchasableOption })}
  >
    <Toolbar>
      <ToolbarTitle
        variant="heading5"
        format="titlecase"
        className={clsx({ 'is-small-caption-tag': !!selectedPurchasableOption && !isActive })}
      >
        room {occupantsIndex + 1}
      </ToolbarTitle>
      {isEditable && !isActive && !!selectedPurchasableOption && <TextButton
        kind="tertiary"
        endIcon={<LineEditAltIcon aria-hidden="true" />}
        aria-label={`edit room ${occupantsIndex + 1}`}
        onClick={handleEdit}
        outdent="right"
      >
        Edit
      </TextButton>}
      {isCheckout && !isEditable && !isActive && !!selectedPurchasableOption && <BodyText
        variant="medium"
      >
        Only room type available
      </BodyText>}
    </Toolbar>
    <RoomDetailContainer>
      <div>
        <SlideDown show={!!selectedPurchasableOption && !isActive}>
          <SelectedRoomTypeNameTitle
            variant="heading5"
            className={clsx({ 'is-expanded': !!selectedPurchasableOption && !isActive })}
          >
            {selectedRoomTypeName}
          </SelectedRoomTypeNameTitle>
        </SlideDown>
        <BodyText variant="large" format="titlecase">{formattedOccupants}</BodyText>
      </div>
      {isCheckout && !isActive && !!selectedPurchasableOption && <div>
        <Heading variant="heading4" as="span" colour="neutral-one">
          <FormatCurrency value={currentSelectedPrice} format="roundedDollar" />
        </Heading>
        <BodyText colour="neutral-two" variant="large" weight="normal" as="span"> total</BodyText>
      </div>}
    </RoomDetailContainer>
    <OptionsList className={clsx({ 'has-children-collapsed': !isActive })}>
      {roomOptions.map(([purchasableOption, priceBreakdown], index) => <RoomTypeOptionCard
        key={index}
        index={index}
        currentSelectedPrice={currentSelectedPrice}
        isCollapsed={!isActive}
        isSelected={purchasableOption === selectedPurchasableOption}
        price={priceBreakdown.totalPrice}
        purchasableOption={purchasableOption}
        onSelect={handlePurchasableOptionSelect}
        onUnselect={handlePurchasableOptionUnselect}
        isCheckout={isCheckout}
      />)}
    </OptionsList>
  </Root>
}

export default RoomOptionsList
